import React from 'react';
import { motion } from 'framer-motion';
import { Home, MessageCircle, ArrowLeft, ShieldAlert } from 'lucide-react';
import { SITE_CONFIG } from '../config/siteConfig';

interface NotFoundPageProps {
    onGoHome: () => void;
    message?: string;
}

export const NotFoundPage: React.FC<NotFoundPageProps> = ({
    onGoHome,
    message = "El modelo de case o la página que buscas no existe, ha cambiado de ubicación o se encuentra temporalmente fuera de línea."
}) => {
    const handleWhatsAppHelp = () => {
        const phone = SITE_CONFIG.contact?.whatsapp || '50379225906';
        const formattedPhone = phone.replace(/[^0-9]/g, '');
        const text = encodeURIComponent('Hola CarbonCase SV, estaba navegando en el catálogo pero no encontré el modelo que buscaba. ¿Podrían orientarme sobre disponibilidad?');
        window.open(`https://wa.me/${formattedPhone}?text=${text}`, '_blank');
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center bg-black relative px-4 py-20 overflow-hidden">
            {/* Hexagonal / Carbon Weave Background Texture */}
            <div className="absolute inset-0 bg-hex-pattern opacity-10 pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/[0.03] rounded-full blur-[140px] pointer-events-none" />

            <div className="max-w-xl w-full text-center relative z-10 space-y-8">
                {/* 404 Badge */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-xs font-mono uppercase tracking-widest text-zinc-400"
                >
                    <ShieldAlert className="w-4 h-4 text-white" />
                    <span>Error 404 — Ruta Inexistente</span>
                </motion.div>

                {/* Giant 404 Header with Metallic Carbon Effect */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                    className="relative"
                >
                    <h1 className="text-8xl sm:text-9xl font-display font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-zinc-400 to-zinc-800 drop-shadow-[0_0_50px_rgba(255,255,255,0.15)] select-none">
                        404
                    </h1>
                    <div className="h-1 w-24 bg-white mx-auto mt-2 rounded-full shadow-[0_0_15px_rgba(255,255,255,0.8)]" />
                </motion.div>

                {/* Text Content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="space-y-3"
                >
                    <h2 className="text-2xl sm:text-3xl font-display font-black uppercase text-white tracking-tight">
                        Pieza o Modelo no Encontrado
                    </h2>
                    <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
                        {message}
                    </p>
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
                >
                    <button
                        onClick={onGoHome}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-zinc-200 text-black font-display font-black uppercase text-xs sm:text-sm tracking-widest py-4 px-8 rounded-sm shadow-[0_0_25px_rgba(255,255,255,0.2)] transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Ver Catálogo Completo</span>
                    </button>

                    <button
                        onClick={handleWhatsAppHelp}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-display font-bold uppercase text-xs sm:text-sm tracking-wider py-4 px-6 rounded-sm border border-zinc-800 hover:border-zinc-600 transition-all"
                    >
                        <MessageCircle className="w-4 h-4 text-[#25D366]" />
                        <span>Preguntar por WhatsApp</span>
                    </button>
                </motion.div>
            </div>
        </div>
    );
};

export default NotFoundPage;
